/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Tag16Regular } from '@fluentui/react-icons';
import { IconButton } from '@fluentui/react/lib/Button';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { default as AutoSizer } from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { CtFiltersPillContainer } from 'src/customText/appShell/dataTaggingPage/components/CtFiltersPillContainer';
import { DocumentFilterDropdown } from 'src/customText/appShell/dataTaggingPage/components/DocumentFilterDropdown';
import { DocumentsFilter } from 'src/customText/appShell/dataTaggingPage/samplerStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

const messages = defineMessages({
    ofItems: { id: 'customText.fileViewer.ofItems', defaultMessage: 'of items' },
    noFilesFound: { id: 'customText.fileViewer.noFilesFound', defaultMessage: 'No files found.' },
    loadMoreFiles: { id: 'customText.fileViewer.loadMoreFiles', defaultMessage: 'Load more files' },
    expandFileViewer: { id: 'customText.fileViewer.expandFileViewer', defaultMessage: 'Expand file viewer' },
    searchPlaceholder: { id: 'customText.fileViewer.searchPlaceholder', defaultMessage: 'Search for a file...' },
    collapseFileViewer: { id: 'customText.fileViewer.collapseFileViewer', defaultMessage: 'Collapse file viewer' }
});

export type FilesViewerProps = {
    query: string;
    isLoading: boolean;
    selectedDocument: string;
    showLoadMoreFiles: boolean;
    areFiltersApplied: boolean;
    isLoadingMoreFiles: boolean;
    isExtractionProject: boolean;
    taggedDocumentNames?: string[];
    handleLoadMoreFiles: () => void;
    appliedFilters?: DocumentsFilter;
    setQuery: (text: string) => void;
    gptTaggedDocumentNames?: string[];
    documents: CustomTextDocumentMetadata[];
    onSelect: (documentName: string) => void;
    onChangeAppliedFilters?: (filters: DocumentsFilter) => void;
};

const viewerTheme = getScopedTheme('FileViewer');
const { getClassName, styleDiv, styleComponent } = getStylistV2('FileViewer');

const Container = styleDiv(
    'Container',
    (theme, cssVars) => ({
        width: cssVars.width,
        display: 'flex',
        flexDirection: 'column',
        borderRight: theme.sidebarBorder
    }),
    {
        cssVars: { width: '' },
        cssVarsSetter: ({ isCollapsed }: { isCollapsed: boolean }) => ({ width: isCollapsed ? '44px' : '200px' })
    }
);

const Header = styleComponent<IStackProps>(Stack)(
    'Header',
    (_, cssVars) => ({
        height: cssVars.height,
        padding: '4px'
    }),
    {
        cssVars: { height: '' },
        cssVarsSetter: ({ areFiltersApplied }: { areFiltersApplied: boolean }) => ({ height: areFiltersApplied ? 110 : 100 })
    }
);

const FilterRow = styleComponent<IStackProps>(Stack)(
    'FilterRow',
    (theme, cssVars) => ({
        borderBottom: cssVars.borderBottom,
        borderColor: theme.borderColor
    }),
    {
        cssVars: { borderBottom: '' },
        cssVarsSetter: ({ areFiltersApplied }: { areFiltersApplied: boolean }) => ({
            borderBottom: areFiltersApplied ? '1px solid' : 'none'
        })
    }
);

const FilesWrapper = styleComponent<IStackProps>(Stack)('FilesWrapper', {
    position: 'relative'
});

const FILE_ROW_HEIGHT = 32;

const FileRow = styleDiv(
    'FileRow',
    (theme, cssVars) => ({
        background: cssVars.background,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 16,
        paddingRight: 16,
        boxSizing: 'border-box',
        cursor: 'pointer',
        $nest: { '&:hover,&:focus': { backgroundColor: theme.fileRowBackgroundColorHovered } }
    }),
    {
        cssVars: { background: '' },
        cssVarsSetter: ({ isSelected }: { isSelected: boolean }, theme) => ({
            background: isSelected ? theme.fileRowBackgroundColorSelected : 'transparent'
        })
    }
);

const LoadMoreLink = styleComponent<ILinkProps>(Link)('LoadMoreLink', (_, cssVars) => ({
    background: cssVars.background,
    display: 'flex',
    paddingLeft: 30,
    paddingRight: 8,
    cursor: 'pointer',
    alignItems: 'center',
    boxSizing: 'border-box'
}));

const LoadingContainer = styleDiv('LoadingContainer', { padding: 12 });
const collapseButtonClassName = getClassName('CollapseButton', { width: 44, height: 44 });
const collapseIconClassName = getClassName('CollapseIcon', { fontSize: 10, color: 'black' });
const noFilesClassName = getClassName('NoFiles', { fontSize: '14px', textAlign: 'center', fontStyle: 'italic', marginTop: 4 });

export const FilesViewer = observer((props: FilesViewerProps) => {
    const {
        query,
        setQuery,
        onSelect,
        isLoading,
        documents,
        appliedFilters,
        selectedDocument,
        showLoadMoreFiles,
        areFiltersApplied,
        isLoadingMoreFiles,
        isExtractionProject,
        handleLoadMoreFiles,
        onChangeAppliedFilters,
        taggedDocumentNames = [],
        gptTaggedDocumentNames = []
    } = props;

    const { localize } = useLocalization();
    const { uiStore, dispatcher } = useGlobalState();
    const { isXXLargeOrGreater } = useResponsiveMode();

    const filteredFiles = substringSearchStrategy(documents, query, 'name');
    const isEmpty = filteredFiles.length === 0;

    const searchButtonIdRef = React.useRef(getId());
    const documentsListRef = React.useRef<FixedSizeList>();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(uiStore.isCustomTextFilesViewerCollapsed);

    React.useEffect(() => {
        dispatcher.dispatch('toggleCustomTextFilesViewer', { isCollapsed: !isXXLargeOrGreater });
    }, [isXXLargeOrGreater]);

    React.useEffect(() => {
        setIsSidebarCollapsed(uiStore.isCustomTextFilesViewerCollapsed);
    }, [uiStore.isCustomTextFilesViewerCollapsed]);

    React.useEffect(() => {
        documentsListRef.current?.scrollToItem(documents.findIndex(document => document.name === selectedDocument));
    }, [selectedDocument]);

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextFilesViewerSearchButton'), []);

    React.useEffect(() => {
        if (
            documents.length &&
            !isSidebarCollapsed &&
            searchButtonIdRef.current &&
            !uiStore.teachingBubbles.CustomTextFilesViewerSearchButton
        ) {
            createTeachingBubble(
                'CustomTextFilesViewerSearchButton',
                localize(customTextTeachingBubbleMessages.filesViewerSearchButtonBody),
                localize(customTextTeachingBubbleMessages.filesViewerSearchButtonTitle),
                `#${searchButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [isSidebarCollapsed, searchButtonIdRef.current, documents, Object.values(uiStore.teachingBubbles)]);

    const headerContent = (() => {
        const fileIconElement = <Icon iconName="FabricFolder" />;
        const searchBoxElement = (
            <LuisDebouncedSearchBox
                showIcon
                autoFocus
                value={query}
                id={searchButtonIdRef.current}
                onChange={(_, val) => setQuery(val)}
                styles={{ root: { maxWidth: '100%' } }}
                ariaLabel={localize(messages.searchPlaceholder)}
                placeholder={localize(messages.searchPlaceholder)}
            />
        );
        if (isSidebarCollapsed) {
            return fileIconElement;
        }

        return (
            <>
                <FilterRow
                    horizontal
                    horizontalAlign={areFiltersApplied ? 'space-between' : 'end'}
                    verticalAlign="center"
                    customProps={{ areFiltersApplied }}>
                    {areFiltersApplied && (
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {documents.length} {localize(messages.ofItems)}
                        </LuisText>
                    )}
                    <DocumentFilterDropdown
                        isExtractionProject={isExtractionProject}
                        appliedFilters={appliedFilters}
                        onChangeFilter={onChangeAppliedFilters}
                    />
                </FilterRow>

                <CtFiltersPillContainer appliedFilters={appliedFilters} onChangeFilter={onChangeAppliedFilters} maxDisplayNumber={1} />
                {searchBoxElement}
            </>
        );
    })();

    const filesContent = (() => {
        if (isSidebarCollapsed) {
            return null;
        }
        if (isLoading) {
            return (
                <LoadingContainer>
                    <ShimmerFactory includeHeaderShimmer={false} shimmerCount={12} shimmerSpacing={15} fullWidth />
                </LoadingContainer>
            );
        }

        if (isEmpty) {
            return <LuisText styles={{ root: noFilesClassName }}>{localize(messages.noFilesFound)}</LuisText>;
        }

        /**
         * If the load more files button is shown, we consider it as an additional item to be
         * rendered in the FixedSizeList components. This way we render this button as the last item
         * in the list.
         * We determine whether the current element is a normal file item or is it the load more button
         * by checking the index against the list of filtered files.
         */

        return (
            <FocusZone style={{ display: 'flex', flex: 1 }}>
                <AutoSizer nonce={window.cspNonce}>
                    {({ height, width }: { height: number; width: number }) => (
                        <FixedSizeList
                            width={width}
                            height={height}
                            ref={documentsListRef}
                            itemSize={FILE_ROW_HEIGHT}
                            itemCount={filteredFiles.length + (showLoadMoreFiles ? 1 : 0)}>
                            {({ index, style }) => {
                                if (index < filteredFiles.length) {
                                    const document = filteredFiles[index];

                                    return (
                                        <FileRow
                                            style={style}
                                            role="button"
                                            key={document.name}
                                            title={document.name}
                                            data-is-focusable="true"
                                            onClick={() => onSelect(document.name)}
                                            customProps={{ isSelected: document.name === selectedDocument }}>
                                            <Stack horizontal tokens={{ childrenGap: 15 }}>
                                                {taggedDocumentNames.includes(document.name) ? (
                                                    <Tag16Regular />
                                                ) : (
                                                    gptTaggedDocumentNames.includes(document.name) && (
                                                        <Icon iconName="DottedTag" styles={{ root: { color: viewerTheme.gptIconColor } }} />
                                                    )
                                                )}
                                                <TruncatedText
                                                    styles={{
                                                        root: {
                                                            paddingLeft:
                                                                taggedDocumentNames.includes(document.name) ||
                                                                gptTaggedDocumentNames.includes(document.name)
                                                                    ? 0
                                                                    : 31
                                                        }
                                                    }}
                                                    fontWeight={
                                                        (document.name === selectedDocument
                                                            ? FontWeights.semibold
                                                            : FontWeights.regular) as Property.FontWeight
                                                    }>
                                                    {document.name}
                                                </TruncatedText>
                                            </Stack>
                                        </FileRow>
                                    );
                                }

                                return (
                                    <LoadMoreLink style={style} onClick={handleLoadMoreFiles} disabled={isLoadingMoreFiles}>
                                        {localize(isLoadingMoreFiles ? globalMessages.loading : messages.loadMoreFiles)}
                                        &nbsp;{isLoadingMoreFiles && <Spinner size={SpinnerSize.small} />}
                                    </LoadMoreLink>
                                );
                            }}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            </FocusZone>
        );
    })();

    return (
        <Container customProps={{ isCollapsed: isSidebarCollapsed }}>
            <Stack horizontalAlign="end">
                <IconButton
                    iconProps={{
                        iconName: isSidebarCollapsed ? 'DoubleChevronLeftMedMirrored' : 'DoubleChevronLeftMed',
                        styles: { root: collapseIconClassName }
                    }}
                    styles={{ root: collapseButtonClassName }}
                    aria-label={isSidebarCollapsed ? localize(messages.expandFileViewer) : localize(messages.collapseFileViewer)}
                    onClick={() => {
                        dispatcher.dispatch('toggleCustomTextFilesViewer', { isCollapsed: !isSidebarCollapsed });
                        if (!isSidebarCollapsed) {
                            dequeueTeachingBubble('CustomTextFilesViewerSearchButton');
                        }
                    }}
                />
            </Stack>

            <Header verticalAlign="center" tokens={{ childrenGap: 4 }} customProps={{ areFiltersApplied }}>
                {headerContent}
            </Header>

            <FilesWrapper grow>{filesContent}</FilesWrapper>
        </Container>
    );
});
